import React from "react"
import { ActionIcon, Badge, CloseButton, Flex, Group } from "@mantine/core"
import { useFormContext } from "react-hook-form"

import { Icon, TextInput } from "@/components"
import { useStyles } from "@/components/SocialMediaInput/SocialMediaInput.styles"
import type { SocialUser } from "@/types/djangio"
import type { SocialMediaInputProps } from "@/components/SocialMediaInput/types"

export const SocialMediaInput = ({
    initialAccounts = [],
    newAccounts = [],
    toDelete,
    error,
    label = "",
    icon,
    iconFamily,
    socialUserType,
    onAdd,
    onDelete,
    onClearError,
}: SocialMediaInputProps) => {
    // This component must be nested in a FormProvider
    const formContext = useFormContext()
    const currentSocialInput = formContext.watch(socialUserType.platform_key, "")
    const isValueEmpty = !(currentSocialInput.length > 0)
    const { classes } = useStyles({ disabled: isValueEmpty })

    const onActionIconClick = () => {
        onClearError(socialUserType.value)

        const values = formContext.getValues()
        const username = values[socialUserType.platform_key]
        const onSuccess = () => formContext.setValue(socialUserType.platform_key, "")
        onAdd(socialUserType, username, onSuccess)
    }

    const onDeleteBadgeClick = (account: Partial<SocialUser>) => {
        onDelete(socialUserType.value, account.id, account.username)
    }

    const onEnterPressed = (e) => {
        if (e.key === "Enter") {
            e.preventDefault()
            onActionIconClick()
        }
    }

    const renderUsername = (username) => {
        if (socialUserType.value === DjangIO.app.social.models.SocialUserType.youtube_user.value) {
            return username
        }

        return `@${username}`
    }

    const accounts = [...initialAccounts.filter((account) => !toDelete.includes(account.id)), ...newAccounts]

    return (
        <Flex direction="column" gap=".5rem">
            {label && <label className={classes.label}>{label}</label>}
            <Flex align="center" gap=".5rem">
                <TextInput
                    className={classes.input}
                    icon={
                        <Icon
                            iconFamily={iconFamily || socialUserType.icon_family}
                            icon={icon || socialUserType.icon}
                            color={socialUserType.color}
                        />
                    }
                    name={socialUserType.platform_key}
                    placeholder={socialUserType.profile_url}
                    onKeyPress={onEnterPressed}
                />
                <ActionIcon
                    className={classes.actionIcon}
                    disabled={isValueEmpty}
                    variant="transparent"
                    onClick={onActionIconClick}
                >
                    <Icon icon="plus" />
                </ActionIcon>
            </Flex>

            {error && (
                <div className={classes.error}>
                    <Icon icon="exclamation-triangle" size="xs" /> {error}
                </div>
            )}

            {accounts.length > 0 && (
                <Group spacing="xs">
                    {accounts.map((account) => (
                        <Badge
                            className={classes.badge}
                            key={`${account.id}${account.username}`}
                            rightSection={
                                <CloseButton variant="transparent" onClick={() => onDeleteBadgeClick(account)} />
                            }
                        >
                            {renderUsername(account.username)}
                        </Badge>
                    ))}
                </Group>
            )}
        </Flex>
    )
}
